import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * A reusable accordion-style section component that displays a header with a title and toggles visibility of its content.
 * @component
 * @selector app-section-accordion
 * @example
 *   <app-section-accordion [title]="'Section Title'" [isExpanded]="false" (toggle)="onSectionToggle()">
 *     <!-- projected content goes here -->
 *   </app-section-accordion>
 * @input {string} title - The text displayed in the section header; used as the section title.
 * @input {boolean} isExpanded - Controls whether the section content is visible (true = expanded, false = collapsed).
 * @output {EventEmitter<void>} toggle - Emitted when the header (or component) is clicked to request toggling the expanded state.
 *
 * Description:
 * - Purpose: Provides a simple, accessible accordion container with a clickable header and optional projected content. The component handles click interactions and emits a toggle event so parent components can synchronize expansion state.
 * - Projected content: Any child content placed inside the component is shown only when isExpanded is true.
 *
 * Key methods:
 * - onToggle(): Emits the toggle event to notify consumers that the user requested a toggle action (e.g., header button clicked).
 *
 * Important lifecycle hooks:
 * - This component does not implement specific Angular lifecycle hooks (ngOnInit/ngOnDestroy). It relies on input bindings and the toggle output for state coordination with parent components.
 */
@Component({
  selector: 'app-section-accordion',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="section-accordion" [class.expanded]="isExpanded" (click)="onToggle()">
      <div class="section-header">
        <h4 class="section-title">{{ title }}</h4>
        <button class="collapse-btn" [class.expanded]="isExpanded" type="button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M4 6L8 10L12 6"
              stroke="#15172E"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              vector-effect="non-scaling-stroke"
            />
          </svg>
        </button>
      </div>

      <div class="section-content" *ngIf="isExpanded" (click)="$event.stopPropagation()">
        <div class="content-divider"></div>
        <ng-content></ng-content>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./section-accordion.component.scss'],
})
export class SectionAccordionComponent {
  @Input() title = '';
  @Input() isExpanded = false;
  @Output() toggle = new EventEmitter<void>();

  onToggle(): void {
    this.toggle.emit();
  }
}
